import axios from 'axios'
import router from '@/router'
import vuecookie from 'vue-cookies'
import { backendUrl } from '@/helpers/backendUrl.js'
import { clientShortName } from '@/helpers/clientShortName.js'

const state = {
    sessionToken: vuecookie.get('token') || null,
    accountEmail: null,
    registrationDetails: JSON.parse(localStorage.getItem('registrationUserDetails')) || null,
    responseInfo: '',
    validationErrors: {},
    spinner: false,
    userDetails: {},
    userLevel: "",
    matchingAccounts: {},
    assignedCustomerRefs: {},
    is_newsnet: false,
    showResponses: [],
    pageIsLoading: false,
    ssoUserCustomerReg: {},
    showEinvoicing: true
};

const getters = {
    //make sure the user is logged in
    loggedIn(state){
        return state.sessionToken != null
    },
    //gets all permited navigation
    getAllowedNavs: (state) => state.userDetails.allowedNav,
    getAccountEmail: (state) => state.accountEmail,
    getRegistrationDetails: (state) => state.registrationDetails,
    getResponseInfo: (state) => state.responseInfo,
    getValidationErrors: (state) => state.validationErrors,
    getSpinner: (state) => state.spinner,
    //gets user details
    getUserDetails: (state) => state.userDetails,
    getMatchingAccounts: (state) => state.matchingAccounts,
    getAssignedCustomerRefs: (state) => state.assignedCustomerRefs,
    //gets display navigation
    getIsNewsnet: (state) => state.is_newsnet || false,
    getShowResponses: (state) => state.showResponses,
    isPageLoading: (state) => state.pageIsLoading,
    getSsoUserCustomerReg: (state) => state.ssoUserCustomerReg,
    getUserLevel: (state) => state.userLevel,
    getUserSessionToken: (state) => state.sessionToken,
    getEinvoicingStatus: (state) => state.showEinvoicing
};

const actions = {
    /**
    * logIn()
    * Called when the user is logging into the system
    */
    logIn(context, credentials, destinationRoute){
        //start spinner
        context.commit('spinerStatus', true)
        let goToRoute = destinationRoute;
        axios.post(`${backendUrl()}/loginUser`,
            {
                username:credentials.loginUsername,
                password:credentials.loginPassword,
            })
            .then(response => {
                //spinner and handle response
                context.dispatch('handleResponse', response)
                context.commit('spinerStatus', false)

                // setting the layout colours for the UI
                context.commit('setLayoutColours', response.data.layoutInfo.colours)

                // setting the layout type for the UI
                context.commit('setLayout', response.data.layoutInfo.layout)

                // set logged in data needed to access the portal for specific login steps
                if(response.data.key == 'userLoggedIn' || response.data.key == 'userLoggedInDeliveryOptions' || response.data.key == 'userLinkAccounts') {
                    //user logged in
                    //set token into cookie. this will be used with requests to identify the user
                    if(process.env.VUE_APP_STATUS && process.env.VUE_APP_STATUS == 'localhost') {
                        vuecookie.set('token', response.data.sessionToken, 60*60*2)
                    } else {
                        vuecookie.set('token', response.data.sessionToken, 60*60*2, null, null, true)
                    }                    

                    //update logIn and userDetails
                    context.commit('logIn', response.data.sessionToken)
                    context.commit('userDetails', response.data.userDetails)
                    context.commit('changeUserLevel', response.data.userDetails.level)

                    //newsnet level (display role switcher)
                    if (response.data.userDetails.level == 3){
                        localStorage.setItem('userLevelName' , 'Newsnet')
                        context.commit('setIsNewsnet', true)
                        localStorage.setItem('userLevel', 3.00)
                    } else if(localStorage.getItem('userLevelName') || localStorage.getItem('userLevel')) {
                        localStorage.removeItem('userLevelName')
                        localStorage.removeItem('userLevel')
                        context.commit('setIsNewsnet', false)
                    } else {
                        context.commit('setIsNewsnet', false)
                    }
                }

                if (response.data.key == 'userLoggedIn'){

                    if(localStorage.getItem('destination') != null){
                        let destination = localStorage.getItem('destination')
                        localStorage.removeItem('destination')
                        router.push({ path: destination})
                    }else{
                        router.push({ name: 'portal'})
                    }

                } else if(response.data.key == 'requirePasswordReset') {
                    //user needs password reset
                    router.push({ name: 'password_reset', params: { token: response.data.token }})
                } else if(response.data.key == 'userLoggedInDeliveryOptions') {
                    //we get the delivery rules in the DeliveryRules component
                    router.push('delivery_rules')
                } else if(response.data.key == 'userLinkAccounts') {
                    // get accounts that use the same VAT number so the user can link them
                    context.commit('matchingAccounts', response.data.matchingAccounts)
                    //context.commit('assignedCustomerRefs', response.data.data.assignedCustomerRefs)
                    router.push('link_accounts')
                } else{
                    console.log('wrong details')
                }
            })
            .catch(() => {
                context.commit('spinerStatus', false)
         })
    },
    /**
    * logOut()
    * Called when the user loggs out
    * Send request to the server to destroy token
    */
    logOut(context, responseKey){
        if( vuecookie.get('token') != undefined ) {
            axios.post(`${backendUrl()}/deleteSessionToken`,
            {
                token: context.getters.allTemplateDetails.token,
                sessionToken: context.state.sessionToken,
                user: context.state.userDetails
            },{
                headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
            })
            .then(response => {
                context.dispatch('handleResponse', response)

                //remove cookie, set state back, remove localstorage and push route
                vuecookie.remove('token')
                context.state.sessionToken = null
                context.state.userDetails = {}
                context.state.isNetsendUser = false
                context.state.navbar = false
                localStorage.removeItem('userLevel')
                localStorage.removeItem('userLevelName')

                if(response.data.sso){
                    window.location.replace(`${backendUrl()}` + '/sso?logout=true')
                    return
                }

                if(responseKey) {
                    context.dispatch('handleTokenExpiration', responseKey)
                } else {
                    context.dispatch('handleTokenExpiration')
                }

                router.push('/')
            })
        } else {
            if(responseKey) {
                context.dispatch('handleTokenExpiration', responseKey)
            } else {
                context.dispatch('handleTokenExpiration')
            }
        }

    },
    /**
    * firstTimeRegister()
    * User able to register after receiving a letter from netsend.
    */
    firstTimeRegister(context, credentials){
        context.commit('spinerStatus', true)
        axios.post(`${backendUrl()}/firstTimeRegister`,
            {
                userEmail: credentials.userEmail,
                userPassword: credentials.userPassword,
                userPasswordConfirm: credentials.userPasswordConfirm,
                userOtherEmails: credentials.userOtherEmails,
                customerId: credentials.customerId,
                clientId: credentials.clientId,
                token:context.getters.allTemplateDetails.token,
                customerReference: credentials.customerReference
            })
            .then(response => {
                context.commit('spinerStatus', false)

                if(response.data.status == 'error' &&  response.data.key == 'validationFail'){
                    context.commit('setValidationErrors',response.data.errors)
                    return;
                }

                context.dispatch('handleResponse', response)
                if(response.data.key == 'userFirstLogin' || response.data.key == 'userLoggedIn' || response.data.key == 'userLoggedInDeliveryOptions') {

                    if(process.env.VUE_APP_STATUS && process.env.VUE_APP_STATUS == 'localhost') {
                        vuecookie.set('token', response.data.sessionToken, 60*60*2)
                    } else {
                        vuecookie.set('token', response.data.sessionToken, 60*60*2, null, null, true)
                    }  

                    context.commit('logIn', response.data.sessionToken)
                    context.commit('userDetails', response.data.userDetails)

                    if (response.data.userDetails.level == 3){
                        localStorage.setItem('userLevelName' , 'Newsnet')
                        context.commit('setIsNewsnet', true)
                        localStorage.setItem('userLevel', 3.00)
                    } else {
                        context.commit('setIsNewsnet', false)
                    }
                }
                if(response.data.key == 'userFirstLogin'){
                    context.dispatch('updateUserRegistrationStep', { step: 2, completed: 0})
                    context.commit('matchingAccounts', response.data.matchingAccounts)
                    //context.commit('assignedCustomerRefs', response.data.assignedCustomerRefs)
                    router.push('link_accounts')
                }  else if(response.data.key == 'userLoggedInDeliveryOptions') {
                    //we get the delivery rules in the DeliveryRules component
                    router.push('delivery_rules')
                } else if (response.data.key == 'userLoggedIn') {
                    router.push('portal')
                }
                else {
                  if(response.data.status != 'error')
                  {
                      router.push('/')
                  }
                }
            })
            .catch(err => {
                context.commit('spinerStatus', false)
            })
    },
    /**
    * changePassword()
    * Change password function. Page will be displayed after user click the link on the e-mail
    */
    changePassword(context, credentials){
        context.commit('spinerStatus', true)
        axios.post(`${backendUrl()}/resetUserPassword`,
            {
                newPassword: credentials.resetPassword,
                newPasswordConfirm: credentials.resetPasswordConfirm,
                resetToken: credentials.token
            })
            .then(response => {
                context.dispatch('handleResponse', response)
                context.commit('spinerStatus', false)

                if(response.data.status == 'success') {
                    router.push('/')
                } else if(response.data.errors) {
                    context.commit('setValidationErrors',response.data.errors)
                }
            })
    },
    /**
    * registerUser()
    * User registration when activated by "I would like to register"
    */
    registerUser(context, data) {
        context.commit('spinerStatus', true)
        let customerData = data.newCustomer;
        let businessUnit = JSON.parse(localStorage.getItem('clientConfig'))
        axios.post(`${backendUrl()}/registerUser`, {
            customerData,
            businessUnit
        }).then(response => {

            if(response.data.status == 'error' &&  response.data.key == 'validationFail'){
                context.commit('setValidationErrors',response.data.errors)
                return;
            }
            context.dispatch('handleResponse', response)
            context.commit('spinerStatus', false)
            if(response.data.status != 'error') {
                router.push('/')
            }
        })
    },
    /**
    * loginWithToken()
    * Probably the most used action from the store. This is called every time the user requires data and is already logged in.
    * It is also used for refreashing the page or loading new database data.
    */
    loginWithToken(context){
        if(vuecookie.get('token') == undefined) {
            router.push('site_unavailable')
        }
        return new Promise((resolve, reject) => {
            axios.post(`${backendUrl()}/loginWithToken`,
                {
                    sessionToken: context.state.sessionToken,
                    userLevel: localStorage.getItem('userLevel') || null
                },{
                        headers: {'Authorization': "bearer " +  vuecookie.get('token')}
                }).then(response => {
                    context.commit('setPageLoading', true)
                    context.commit('userDetails', response.data)
                    context.commit('changeUserLevel', response.data.level)

                    if (response.data.level == 3){
                        localStorage.setItem('userLevelName' , 'Newsnet')
                        context.commit('setIsNewsnet', true)
                        localStorage.setItem('userLevel', 3.00)
                    } else {
                        context.commit('setIsNewsnet', false)
                    }

                    resolve(response.data.userDetails)
                })
                .catch(err => {
                    console.log('Auth error')
                    reject(err)
            })
        })
    },
    /**
    * changeNewsnetUserLevel()
    * Switch between user levels
    */
    changeNewsnetUserLevel(context,data){
        context.commit('spinerStatus', true)
        axios.post(`${backendUrl()}/changeNewsnetUserLevel`,
            {
                sessionToken: context.state.sessionToken,
                userLevel: data.level || null
            },{
                headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
            })
            .then(response => {
                context.commit('userDetails', response.data)
                context.commit('changeUserLevel', response.data.newUserLevel)
                context.commit('spinerStatus', false)
            })
    },
    linkUserAccounts(context,data){
        axios.post(`${backendUrl()}/linkUserAccounts`,
            {
                token:context.getters.allTemplateDetails.token,
                sessionToken: context.state.sessionToken,
                userId: context.getters.getUserDetails.id,
                customerRefs: data.customerRefs
            },{
                headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
            })
            .then(response => {
                if(response.data.status == 'error') {
                    context.dispatch('handleResponse', response)
                } else {
                    context.dispatch('updateUserRegistrationStep', { step: 3, completed: 0}).then(response => {
                        //we get the delivery rules in the DeliveryRules component
                        router.push('delivery_rules')
                    })
                }
            })
    },
    requestMatchingAccounts( context ) {
        axios.post(`${backendUrl()}/requestMatchingAccounts`,
            {
                token:context.getters.allTemplateDetails.token,
                sessionToken: context.state.sessionToken,
                userDetails: context.state.userDetails
            },{
                headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
            })
            .then(response => {
                context.commit('matchingAccounts', response.data)
            })
    },
    async updateUserRegistrationStep(context, data) {
        await axios.post(`${backendUrl()}/updateUserRegistrationStep`,
        {
            token:context.getters.allTemplateDetails.token,
            sessionToken: context.state.sessionToken,
            userId: context.getters.getUserDetails.id,
            stepId: data.step,
            completed: data.completed
        },{
            headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
        })
    },

    ssoRegistration(context, data) {
        let newUser = data.newUser
        axios.post(`${backendUrl()}/ssoRegistration`, newUser ).then(response => {
            context.dispatch('handleResponse', response)

            if(response.data.key == 'ssoUserAlreadyRegistered') {
                context.commit('accountEmailUsed', response.data.emailUsed)
                router.push({ name: 'user_exists' })
            } else if(response.data.key == 'ssoRegisterNewCustomerUser') {
                context.commit('setSsoUserCustomerReg', { customerRef: response.data.customerRef, userEmail: response.data.emailUsed })
                router.push({ name: 'ssoAlreadyRegistered'})
            }

        })
    },

    ssoConfirmUserCustomerLink(context, data) {
        axios.post(`${backendUrl()}/ssoLinkUserCustomer`, {
            userEmail: data.userEmail,
            customerRef: data.customerRef,
            userPassword: data.userPassword
        }).then(response => {
            context.dispatch('handleResponse', response)
            if(response.data.key == 'linkDataSuccess') {
                router.push({ name: 'ssoRegistrationSuccess' })
            }
        })
    },

    /**
    * handleResponse()
    * handle error or warning messages
    */
    handleResponse(context, response) {
        if(response.data) {
            // EIPP-74 Clear response before setting it again ( should fix responses sometimes not showing up ), PW
            context.dispatch('alertClearResponse').then(() => {
                if(response.data.httpCode && response.data.httpCode == 503){
                    router.push('/site_unavailable')
                } else if (response.status && response.status == 401) {
                    // if user's token expired and that user sends request to the server then log him/her out
                    context.dispatch('handleTokenExpiration')
                } else {
                    context.commit('responseInfo', response.data)
                    context.commit('setShowResponses', [])
                }
            })
        }
    },

    handleTokenExpiration(context, responseKey = null) {
        vuecookie.remove('token')

        context.state.sessionToken = null
        context.state.userDetails = {}
        context.state.is_newsnet = false
        context.state.userLevel = ""
        context.state.isNetsendUser = false
        context.state.navbar = false

        localStorage.removeItem('userLevel')
        localStorage.removeItem('userLevelName')

        context.commit('setLoggedIn', false)

        if(responseKey) {
            context.commit('responseInfo', { key: responseKey, status: 'error', responseName: 'login' })
        } else {
            context.commit('responseInfo', { key: 'userSessionExpired', status: 'error', responseName: 'login' })
        }
        
        router.push('/')
    },

    async updateQuickLinks(context, data) {
        await axios.post(`${backendUrl()}/updateQuickLinks`, {
            destination: data}, {
            headers: { 'Authorization': "bearer " +  vuecookie.get('token')}
        })
    },

    async updateLastRequest(context, data) {
        // if token is set send a request to update it
        if(vuecookie.get('token') != undefined) {
          return await axios.post(`${backendUrl()}/updateLastRequest`,
                {
                    userId: context.getters.getUserDetails.id
                },
                {
                    headers: { 'Authorization': "bearer " + vuecookie.get('token') }
                }).then(response => {
                if(response.data.status && response.data.status == 'success') {
                    if(process.env.VUE_APP_STATUS && process.env.VUE_APP_STATUS == 'localhost') {
                        vuecookie.set('token', response.data.newToken, 60*60*2)
                    } else {
                        vuecookie.set('token', response.data.newToken, 60*60*2, null, null, true)
                    }
                }
              return response
            })
        } else {
            // if token is not set then handle token and data deletion so the user will be logged out afterwards
            context.dispatch('handleTokenExpiration')
        }

        return null
    },

    redirectToV1(context, destination) {
        axios.post(`${backendUrl()}/redirectToV1`,
        {
            userId: context.getters.getUserDetails.id,
            clientId: context.getters.allTemplateDetails.id
        },
        {
            headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
        }).then(response => {
            if(response.data.status == 'success') {
                // prototype redirect url
                window.location.replace(response.data.base_href + destination + '?hash=' + response.data.hash)
            } else {
                console.log('Failed to redirect user to V1, redirecting back to portal page...')
                router.push('/')
            }
        })
    },

    async loginFromV1(context, data) {
        return await axios.post(`${backendUrl()}/loginFromV1`,
        {
            hash_token: data.hash_token,
            business_unit: data.business_unit
        }, null).then(response => {
            if(response.data && response.data.status && response.data.status == 'success') {
                vuecookie.set('token', response.data.token, 60*60*2)
                context.commit('logIn', response.data.token)

                if(data.business_unit) {
                    context.dispatch('loginWithToken').then(() => {
                        context.dispatch('setBusinessUnitFromUrl', data.business_unit)
                    })
                } else {
                    context.dispatch('loginWithToken').then(() => {
                        router.push('/')
                    })
                }                
            } else {
                return response.data
            }
        })
    },

    async customerSignup(context, data) {
        await axios.post(`${backendUrl()}/customerSignup`,
            {
                username: data.customerUsername,
                password: data.customerPassword,
            })
            .then(response => {
                context.dispatch('handleResponse', response)
                context.commit('spinerStatus', false)

                if (response.data) {
                    if(response.data.loginStatus == "registerUser") {
                        context.commit('registrationUserDetails', response.data.customerDetails)
                        router.push('registration_required')
                    } else if(response.data.loginStatus == 'customerUserAlreadyRegistered'){
                        //user already exists
                        context.commit('accountEmailUsed', response.data.customerDetails.userEmail)
                        router.push('user_exists')
                    }
                }
            })
            .catch(() => {
                context.commit('spinerStatus', false)
         })
    },

    verifyPasswordResetToken(context, data) {
        axios.post(`${backendUrl()}/verifyPasswordResetToken`, {
            token: data.token,
            userId: data.userId
        }, 
        {
            headers: { 'Authorization': "bearer " +  vuecookie.get('token') }
        }).then(response => {
            if(response.data.status == 'error') {
                context.dispatch('handleResponse', response)
                router.push('/') 
            }
        })
    },

    alertClearResponse(context) {
        context.commit('alertClearResponse')
    },

    alertClearErrors(context) {
        context.commit('alertClearErrors')
    },
    alertClearArrayResponses(context, key) {
        let responseObject = context.state.responseInfo.arrayResponses
        responseObject[key] = {}
        delete responseObject[key]
        context.commit('alertClearArrayResponses', responseObject)
    }
};

//Data can be modified here before updating state
const mutations = {
    logIn: (state, sessionToken) => (state.sessionToken = sessionToken),
    accountEmailUsed: (state, accountEmail) => (state.accountEmail = accountEmail),
    registrationUserDetails: (state, registrationDetails) => (state.registrationDetails = registrationDetails),
    userDetails: (state, userDetails) => (state.userDetails = userDetails),
    matchingAccounts: (state, matchingAccounts) => (state.matchingAccounts = matchingAccounts),
    assignedCustomerRefs: (state, assignedCustomerRefs) => (state.assignedCustomerRefs = assignedCustomerRefs),
    spinerStatus: (state, spinner) => (state.spinner = spinner),
    responseInfo: (state, responseInfo) => (state.responseInfo = responseInfo),
    setShowResponses: (state, showResponses) => (state.showResponses = showResponses),
    setValidationErrors: (state, validationErrors) => (state.validationErrors = validationErrors),
    setIsNewsnet: (state, is_newsnet) => (state.is_newsnet = is_newsnet),
    setUserProfilePicture: (state, pictureUrl) => (state.userDetails.profilePicture = pictureUrl),
    clearResponse: () => (state, responseInfo) => (state.responseInfo = ''),
    alertClearResponse: () => (state.responseInfo = '' ),
    alertClearErrors: () => (state.validationErrors = {} ),
    alertClearArrayResponses: (state, responses) => (state.responseInfo.arrayResponses = responses),
    setLoggedIn: (state, status) => (state.loggedIn = status),
    setPageLoading: (state, status) => (state.pageIsLoading = status),
    setSsoUserCustomerReg: (state, status) => (state.ssoUserCustomerReg = status),
    changeUserLevel: (state, userLevel) => (state.userLevel = userLevel),
    setNavigation: (state, nav) => (state.userDetails.navbar = nav),
    setAllowedNavigation: (state, nav) => (state.userDetails.allowedNav = nav),
    setEinvoicingStatus: (state, einv) => (state.showEinvoicing = einv),
    setRedirectionLink: (state, redirectionLink) => (state.userDetails.v1_redirection = redirectionLink)
};

export default {
  state,
  getters,
  actions,
  mutations
}
